import * as React from 'react';
import { 
    Box, 
    Paper, 
    Typography, 
    Checkbox, 
    IconButton, 
    Button, 
    Chip,
    Card,
    CardContent,
    Divider,
    Fade,
    TextField,
    InputAdornment,
    Tooltip
} from '@mui/material';
import { 
    Edit, 
    Delete, 
    CheckCircle, 
    HourglassEmpty,
    Euro,
    MonitorWeight,
    ShoppingBasket,
    Search,
    Info
} from '@mui/icons-material';

import Events from '../service/Events';
import ClientAPI from '../service/ClientAPI';

import UpdateCustomerProductsPopup from './UpdateCustomerProductsPopup';

const CartToolbar = (props) => {
  const { numSelected, onEdit, onConfirm, onDeleteOrders, orders, onSelectAll, hasUnconfirmed, searchTerm, onSearchChange } = props;
  
  const totalPrice = orders.length ? orders.reduce((sum, o) => {
    if (o.isFixed) {
      return sum + (o.Price * o.Amount);
    } else {
      return sum + (o.CustomPrice || 0);
    }
  }, 0).toFixed(2) : 0;

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 3
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
          p: { xs: 2, sm: 2.5 },
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <ShoppingBasket sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              {numSelected > 0 ? `${numSelected} Artikel ausgewählt` : 'Warenkorb Übersicht'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {orders.length} {orders.length === 1 ? 'Artikel' : 'Artikel'} insgesamt
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Gesamtpreis
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              {totalPrice}€
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action Bar */}
      {numSelected > 0 ? (
        <Box
          sx={{
            bgcolor: '#e8f5e9',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            {numSelected} Artikel für Aktionen ausgewählt
          </Typography>
          
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<CheckCircle sx={{ display: { xs: 'none', sm: 'block' } }} />}
              onClick={onConfirm}
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{
                bgcolor: '#2e7d32',
                '&:hover': { bgcolor: '#1b5e20' },
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Bestätigen
            </Button>
            <Button
              variant="outlined"
              startIcon={<Edit sx={{ display: { xs: 'none', sm: 'block' } }} />}
              onClick={onEdit}
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{
                borderColor: '#2e7d32',
                color: '#2e7d32',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': { borderColor: '#1b5e20', bgcolor: '#f1f8e9' }
              }}
            >
              Bearbeiten
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete sx={{ display: { xs: 'none', sm: 'block' } }} />}
              onClick={() =>
                Events.onAlert.emit("warning", "Möchten Sie alle ausgewählten Bestellungen löschen?", 
                  "Wenn Sie alle ausgewählten Bestellungen löschen, können Sie dieses Element nicht mehr bearbeiten und müssen sich das Produkt erneut hinzufügen", 
                  onDeleteOrders)
              }
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Löschen
            </Button>
          </Box>
        </Box>
      ) : <></>}

      {/* Search Bar */}
      <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
        <TextField
          fullWidth
          placeholder="Produkte durchsuchen..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#81c784' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'white',
              '&:hover fieldset': {
                borderColor: '#81c784',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#2e7d32',
              },
            },
          }}
        />
      </Box>

      {/* Select All Option */}
      {hasUnconfirmed ? (
        <Box
          sx={{
            bgcolor: '#f5f5f5',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#eeeeee' }
          }}
          onClick={onSelectAll}
        >
          <Checkbox
            checked={numSelected > 0 && numSelected === orders.filter(o => !o.Confirmed).length}
            indeterminate={numSelected > 0 && numSelected < orders.filter(o => !o.Confirmed).length}
            sx={{
              color: '#2e7d32',
              '&.Mui-checked': { color: '#2e7d32' }
            }}
          />
          <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
            Alle nicht bestätigten Artikel auswählen
          </Typography>
        </Box>
      ) : <></>}
    </Paper>
  );
};

export default class MyProductsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selected: [],
      orders: [],
      show: false,
      searchTerm: '',
      editingOrder: null
    };
  }

  componentDidUpdate() {
    if (this.state.orders !== this.props.orders) {
      this.setState({ orders: this.props.orders })
    }
  }

  handleSelectAllClick = (event) => {
    if (this.state.selected == null || this.state.selected.length <= 0) {
      const filteredOrders = this.getFilteredOrders().filter(_order => !_order.Confirmed);
      this.setState({ selected: filteredOrders });
      return;
    }

    this.setState({ selected: [] });
  };

  handleSearchChange = (value) => {
    this.setState({ searchTerm: value });
  };

  getFilteredOrders = () => {
    if (!this.state.searchTerm) {
      return this.state.orders;
    }
    return this.state.orders.filter(order => 
      order.Name.toLowerCase().includes(this.state.searchTerm.toLowerCase())
    );
  };

  handleEditOrder = (order, event) => {
    event.stopPropagation(); // Prevent card selection
    this.setState({ editingOrder: [order], show: true });
  };

  handleDeleteOrder = (order, event) => {
    event.stopPropagation(); // Prevent card selection
    Events.onAlert.emit(
      "warning", 
      "Möchten Sie diese Bestellung löschen?", 
      "Wenn Sie diese Bestellung löschen, können Sie dieses Element nicht mehr bearbeiten und müssen sich das Produkt erneut hinzufügen",
      () => {
        ClientAPI.DeleteOrders({ orders: [order] })
          .then(() => {
            window.location.reload();
          })
          .catch(console.error);
      }
    );
  };

  handleClick = (event, id) => {
    const selectedIndex = this.state.orders.findIndex(p => p.orderID === id);

    if (selectedIndex === -1) {
      return;
    }

    if (this.state.orders[selectedIndex].Confirmed) {
      return;
    }

    const copiedArr = Array.from(this.state.selected);

    if (copiedArr.length <= 0) {
      copiedArr.push(this.state.orders[selectedIndex]);

      this.setState({ selected: copiedArr });
      return;
    }

    const ind = copiedArr.findIndex(p => p.orderID === id);

    if (ind !== -1) {
      copiedArr.splice(ind, 1)
    } else {
      copiedArr.push(this.state.orders[selectedIndex]);
    }

    this.setState({ selected: copiedArr });
  };

  isSelected = (id) => this.state.selected.findIndex(s => s.orderID === id) !== -1;

  deleteOrders = () => {
    ClientAPI.DeleteOrders({ orders: this.state.selected })
      .then(() => {
        window.location.reload();
      })
      .catch(console.error)
  }

  confirmOrders = () => {
    if (!this.state.selected || this.state.selected.length === 0) {
      Events.onResult.emit("error", "Bitte wählen Sie mindestens eine Bestellung aus, um sie zu bestätigen!");
      return;
    }

    Events.onAlert.emit('warning', "Möchten Sie alle ausgewählten Bestellungen bestätigen?", "Wenn Sie die Bestellungen bestätigen, können Sie die Bestellungen nicht mehr bearbeiten und auch keine neuen Produkte, von dieser Art, hinzufügen!", () => {
      ClientAPI.ConfirmOrders(this.state.selected.map(order => order.orderID))
        .then(() => {
          Events.onResult.emit("success", "Bestellungen wurden erfolgreich bestätigt!");
          setTimeout(() => {
            window.location.reload();
          }, 500);
        })
        .catch((error) => {
          console.error(error);
          Events.onResult.emit("error", "Fehler beim Bestätigen der Bestellungen");
        });
    });
  }

  displayOrderData = (order, amount, type) => order.isFixed ? `${((type === "€" ? order.Price : order.Weight) * amount).toFixed(2)}${type}` : order.CustomPrice !== 0 ? `${type === "€" ? order.CustomPrice : order.CustomWeight}${type}` : "wird berechnet...";

  render() {
    const hasUnconfirmed = this.state.orders.some(o => !o.Confirmed);
    const filteredOrders = this.getFilteredOrders();
    
    return (
      <>
        <Box sx={{ width: '100%', overflowX: 'hidden' }}>
          <CartToolbar 
            orders={filteredOrders} 
            onConfirm={this.confirmOrders} 
            numSelected={this.state.selected.length} 
            onEdit={() => this.setState({ editingOrder: this.state.selected, show: true })} 
            onDeleteOrders={() => this.deleteOrders()}
            onSelectAll={this.handleSelectAllClick}
            hasUnconfirmed={hasUnconfirmed}
            searchTerm={this.state.searchTerm}
            onSearchChange={this.handleSearchChange}
          />

          {/* Product Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredOrders.length === 0 && this.state.orders.length === 0 ? (
              <Paper
                elevation={2}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  borderRadius: '12px',
                  bgcolor: '#f5f5f5'
                }}
              >
                <ShoppingBasket sx={{ fontSize: '5rem', color: '#bdbdbd', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#666', mb: 1 }}>
                  Ihr Warenkorb ist leer
                </Typography>
                <Typography variant="body1" sx={{ color: '#999' }}>
                  Fügen Sie Produkte hinzu, um mit Ihrer Bestellung zu beginnen
                </Typography>
              </Paper>
            ) : filteredOrders.length === 0 ? (
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: '12px',
                  bgcolor: '#f5f5f5'
                }}
              >
                <Search sx={{ fontSize: '3rem', color: '#bdbdbd', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                  Keine Produkte gefunden
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Versuchen Sie einen anderen Suchbegriff
                </Typography>
              </Paper>
            ) : (
              filteredOrders.map((order, index) => {
                const isItemSelected = this.isSelected(order.orderID);
                const totalPrice = this.displayOrderData(order, !order.isFixed ? 1 : order.Amount, "€");
                const totalWeight = this.displayOrderData(order, !order.isFixed ? 1 : order.Amount, "Kg/L");

                return (
                  <Fade in={true} key={order.orderID} timeout={200 + (index * 50)}>
                    <Card
                      elevation={isItemSelected ? 6 : 2}
                      sx={{
                        borderRadius: { xs: '12px', sm: '10px' },
                        transition: 'all 0.2s ease',
                        cursor: 'default',
                        opacity: order.Confirmed ? 0.7 : 1,
                        border: isItemSelected ? '2px solid #2e7d32' : order.Confirmed ? '2px solid #9e9e9e' : '2px solid transparent',
                        '&:hover': {
                          boxShadow: order.Confirmed ? undefined : '0 4px 16px rgba(0, 0, 0, 0.1)'
                        },
                        bgcolor: order.Confirmed ? '#f5f5f5' : 'white'
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                        {/* Desktop Layout */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
                          {/* Checkbox */}
                          <Box onClick={(e) => e.stopPropagation()}>
                            {!order.Confirmed ? (
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => this.handleClick(event, order.orderID)}
                                size="small"
                                sx={{
                                  color: '#2e7d32',
                                  '&.Mui-checked': { color: '#2e7d32' }
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#e0e0e0',
                                  borderRadius: '50%'
                                }}
                              >
                                <CheckCircle sx={{ color: '#757575', fontSize: '1.2rem' }} />
                              </Box>
                            )}
                          </Box>

                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: '8px',
                              bgcolor: '#e8f5e9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <ShoppingBasket sx={{ fontSize: '1.5rem', color: '#81c784' }} />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                              {order.Name}
                            </Typography>
                            <Chip
                              icon={order.Confirmed ? <CheckCircle /> : <HourglassEmpty />}
                              label={order.Confirmed ? "Bestätigt" : "In Bearbeitung"}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '0.7rem',
                                bgcolor: order.Confirmed ? '#e0e0e0' : '#fff3e0',
                                color: order.Confirmed ? '#757575' : '#e65100',
                                fontWeight: 600,
                                '& .MuiChip-icon': {
                                  fontSize: '0.9rem',
                                  color: order.Confirmed ? '#757575' : '#e65100'
                                }
                              }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.65rem' }}>
                                Anzahl
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {order.Amount}x
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.65rem' }}>
                                Einzelpreis
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {this.displayOrderData(order, 1, "€")}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.65rem' }}>
                                Gewicht
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                {totalWeight}
                              </Typography>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.65rem' }}>
                                Gesamt
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                {totalPrice}
                              </Typography>
                            </Box>
                          </Box>

                          {!order.Confirmed && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                startIcon={<Edit />}
                                onClick={(e) => this.handleEditOrder(order, e)}
                                sx={{
                                  bgcolor: '#2e7d32',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  px: 2,
                                  py: 1,
                                  minWidth: '110px',
                                  fontSize: '0.875rem',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                                  '&:hover': {
                                    bgcolor: '#1b5e20',
                                    boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)',
                                    transform: 'translateY(-2px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Bearbeiten
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<Delete />}
                                onClick={(e) => this.handleDeleteOrder(order, e)}
                                sx={{
                                  bgcolor: '#d32f2f',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  px: 2,
                                  py: 1,
                                  minWidth: '110px',
                                  fontSize: '0.875rem',
                                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                                  '&:hover': {
                                    bgcolor: '#c62828',
                                    boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
                                    transform: 'translateY(-2px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Löschen
                              </Button>
                            </Box>
                          )}
                        </Box>

                        {/* Mobile Layout - Completely Redesigned */}
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                          {/* Header Row with Checkbox and Status */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                              {!order.Confirmed ? (
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={(event) => this.handleClick(event, order.orderID)}
                                  size="small"
                                  sx={{
                                    color: '#2e7d32',
                                    '&.Mui-checked': { color: '#2e7d32' },
                                    p: 0.5
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#e0e0e0',
                                    borderRadius: '50%'
                                  }}
                                >
                                  <CheckCircle sx={{ color: '#757575', fontSize: '1rem' }} />
                                </Box>
                              )}
                              
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 0.5, fontSize: '0.95rem', lineHeight: 1.2 }}>
                                  {order.Name}
                                </Typography>
                                <Chip
                                  icon={order.Confirmed ? <CheckCircle /> : <HourglassEmpty />}
                                  label={order.Confirmed ? "Bestätigt" : "In Bearbeitung"}
                                  size="small"
                                  sx={{
                                    height: '18px',
                                    fontSize: '0.65rem',
                                    bgcolor: order.Confirmed ? '#e0e0e0' : '#fff3e0',
                                    color: order.Confirmed ? '#757575' : '#e65100',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': {
                                      fontSize: '0.75rem',
                                      color: order.Confirmed ? '#757575' : '#e65100'
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>

                          {/* Info Grid */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: 1.5, 
                            mb: 2,
                            bgcolor: '#f8fdf9',
                            p: 1.5,
                            borderRadius: '8px'
                          }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.7rem', mb: 0.3 }}>
                                Anzahl
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1rem' }}>
                                {order.Amount}x
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.7rem', mb: 0.3 }}>
                                Gesamt
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1rem' }}>
                                {totalPrice}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Action Buttons - Full Width */}
                          {!order.Confirmed && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                onClick={(e) => this.handleEditOrder(order, e)}
                                fullWidth
                                sx={{
                                  bgcolor: '#2e7d32',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  py: 1.2,
                                  fontSize: '0.85rem',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                                  '&:hover': {
                                    bgcolor: '#1b5e20'
                                  }
                                }}
                              >
                                Bearbeiten
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={(e) => this.handleDeleteOrder(order, e)}
                                fullWidth
                                sx={{
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  py: 1.2,
                                  fontSize: '0.85rem',
                                  '&:hover': {
                                    borderColor: '#c62828',
                                    bgcolor: 'rgba(211, 47, 47, 0.04)'
                                  }
                                }}
                              >
                                Löschen
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                );
              })
            )}
          </Box>
        </Box>
        <UpdateCustomerProductsPopup 
          show={this.state.show} 
          orders={this.state.editingOrder || this.state.selected} 
          onClose={() => this.setState({ show: false, editingOrder: null })} 
        />
      </>
    );
  }
}